/**
 * Research Agent Service
 * Core orchestrator for autonomous research pipeline
 *
 * Pipeline: Planning → Searching → Extracting → Drafting → Critiquing → Refining
 */

import { perplexityService } from '@services/api/perplexity.service';
import { tavilyService } from './tavilyService';
import { researchStorageService } from './researchStorage';
import {
  ResearchEvent,
  ResearchQuestion,
  ResearchSource,
  DraftCritique,
  AgentStepType,
  ResearchMetadata,
  ResearchSession,
  ResearchServiceResponse,
} from '@/types/research.types';

class ResearchAgent {
  /**
   * Main research method - generates async events for UI streaming
   *
   * @param topic - Research topic
   * @param userId - Current user ID
   * @returns AsyncGenerator of research events
   */
  async *research(topic: string, userId: string): AsyncGenerator<ResearchEvent> {
    const startTime = Date.now();
    let totalCost = 0;
    const stepDetails: any[] = [];

    // Create session in database
    const sessionResponse = await researchStorageService.createSession(userId, topic);
    if (sessionResponse.error || !sessionResponse.data) {
      yield this.createEvent('planning', 'error', null, sessionResponse.error?.message);
      return;
    }

    const sessionId = sessionResponse.data.id;

    try {
      // STEP 1: Planning - Generate research questions
      yield this.createEvent('planning', 'in_progress', { sessionId });
      const stepStart = Date.now();

      const questionsResult = await this.generateQuestions(topic);
      if (questionsResult.error || !questionsResult.data) {
        yield this.createEvent('planning', 'error', null, questionsResult.error?.message);
        return;
      }

      const questions = questionsResult.data;
      const planningCost = 0.01; // Estimated cost
      totalCost += planningCost;

      stepDetails.push({
        type: 'planning',
        status: 'complete',
        startTime: stepStart,
        endTime: Date.now(),
        cost: planningCost,
        data: { outputPreview: questions.map(q => q.question).slice(0, 5) },
      });

      yield this.createEvent('planning', 'complete', { questions });
      // Persist after planning
      await researchStorageService.updateSession(sessionId, {
        status: 'in_progress',
        questions,
      });

      // STEP 2: Searching - Query web for each question (stream progress)
      yield this.createEvent('searching', 'in_progress', {
        total: questions.length,
        progress: `0/${questions.length}`,
      });

      const searchStart = Date.now();
      const queries = questions.map(q => q.question);
      const aggregatedResults: any[] = [];
      for (let i = 0; i < queries.length; i++) {
        const q = queries[i];
        // Emit progress update
        yield this.createEvent('searching', 'in_progress', {
          progress: `${i + 1}/${queries.length}`,
          message: `Searching: ${q}`,
        });
        const res = await tavilyService.search(q, {
          maxResults: 3,
          searchDepth: 'basic',
        });
        if (res.data) {
          aggregatedResults.push(...res.data);
        }
        // If a single query fails, continue; overall error only if none succeed
      }
      const uniqueSources = tavilyService.convertToResearchSources(aggregatedResults, 0.5);
      const sources = uniqueSources.slice(0, 10);
      const searchingCost = 0.0; // Tavily free tier
      totalCost += searchingCost;

      stepDetails.push({
        type: 'searching',
        status: 'complete',
        startTime: searchStart,
        endTime: Date.now(),
        cost: searchingCost,
        data: { outputPreview: sources.map(s => s.title).slice(0, 5) },
      });

      yield this.createEvent('searching', 'complete', { sources });
      // Persist after searching
      await researchStorageService.updateSession(sessionId, {
        status: 'in_progress',
        sources,
        metadata: {
          cost: totalCost,
          duration: (Date.now() - startTime) / 1000,
          model: 'sonar',
          questionsCount: questions.length,
          sourcesCount: sources.length,
          stepDetails,
        } as any,
      });

      // STEP 3: Extracting - Summarize each source
      yield this.createEvent('extracting', 'in_progress', {
        total: sources.length,
        progress: `0/${sources.length}`,
      });

      const extractStart = Date.now();
      const extractedSources: ResearchSource[] = [];
      const partialExtracted: Array<{ title: string; summary: string; sourceName?: string }> = [];
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i];
        // Emit progress with the target being processed
        yield this.createEvent('extracting', 'in_progress', {
          progress: `${i + 1}/${sources.length}`,
          message: `Extracting: ${source.sourceName || source.title}`,
          // Provide last few partial summaries for UI to render while running
          partialExtracted: partialExtracted.slice(-3),
        });

        const prompt = `Extract key insights from this source relevant to "${topic}":

Source: ${source.title}
Content: ${source.summary}

Provide a concise summary (2-3 sentences) of the most important information.`;

        const response = await perplexityService.askQuestion(prompt);
        if (response.data) {
          const enriched = {
            ...source,
            summary: response.data.content,
          };
          extractedSources.push(enriched);
          partialExtracted.push({
            title: enriched.title,
            summary: enriched.summary,
            sourceName: enriched.sourceName,
          });
        } else {
          extractedSources.push(source);
          partialExtracted.push({
            title: source.title,
            summary: source.summary,
            sourceName: source.sourceName,
          });
        }
        // Emit updated partials after each item
        yield this.createEvent('extracting', 'in_progress', {
          progress: `${i + 1}/${sources.length}`,
          partialExtracted: partialExtracted.slice(-3),
        });
      }
      const extractingCost = sources.length * 0.004; // ~$0.004 per extraction
      totalCost += extractingCost;

      stepDetails.push({
        type: 'extracting',
        status: 'complete',
        startTime: extractStart,
        endTime: Date.now(),
        cost: extractingCost,
        data: { outputPreview: extractedSources.map(s => s.title).slice(0, 5) },
      });

      yield this.createEvent('extracting', 'complete', { sources: extractedSources });
      // Persist after extracting
      await researchStorageService.updateSession(sessionId, {
        status: 'in_progress',
        sources: extractedSources,
        metadata: {
          cost: totalCost,
          duration: (Date.now() - startTime) / 1000,
          model: 'sonar',
          questionsCount: questions.length,
          sourcesCount: extractedSources.length,
          stepDetails,
        } as any,
      });

      // STEP 4: Drafting - Write initial report
      yield this.createEvent('drafting', 'in_progress');

      const draftStart = Date.now();
      const draftResult = await this.draftReport(topic, questions, extractedSources);
      if (draftResult.error || !draftResult.data) {
        yield this.createEvent('drafting', 'error', null, draftResult.error?.message);
        return;
      }

      const draft = draftResult.data.content;
      const draftCitations = draftResult.data.citations || [];
      const draftingCost = 0.01;
      totalCost += draftingCost;

      stepDetails.push({
        type: 'drafting',
        status: 'complete',
        startTime: draftStart,
        endTime: Date.now(),
        cost: draftingCost,
        data: { outputPreview: draft.slice(0, 300), draftFull: draft, citations: draftCitations },
      });

      yield this.createEvent('drafting', 'complete', { draft, citations: draftCitations });
      // Persist after drafting (preview only)
      await researchStorageService.updateSession(sessionId, {
        status: 'in_progress',
        metadata: {
          cost: totalCost,
          duration: (Date.now() - startTime) / 1000,
          model: 'sonar',
          questionsCount: questions.length,
          sourcesCount: extractedSources.length,
          stepDetails,
        } as any,
      });

      // STEP 5: Critiquing - Review draft quality
      yield this.createEvent('critiquing', 'in_progress');

      const critiqueStart = Date.now();
      const critiqueResult = await this.critiqueDraft(draft, topic, questions);
      if (critiqueResult.error || !critiqueResult.data) {
        yield this.createEvent('critiquing', 'error', null, critiqueResult.error?.message);
        return;
      }

      const critique = critiqueResult.data;
      const critiquingCost = 0.01;
      totalCost += critiquingCost;

      stepDetails.push({
        type: 'critiquing',
        status: 'complete',
        startTime: critiqueStart,
        endTime: Date.now(),
        cost: critiquingCost,
        data: {
          outputPreview: {
            overall: critique.overallQuality,
            strengths: critique.strengths?.slice(0, 3),
            weaknesses: critique.weaknesses?.slice(0, 3),
          },
        },
      });

      yield this.createEvent('critiquing', 'complete', { critique });
      // Persist after critiquing
      await researchStorageService.updateSession(sessionId, {
        status: 'in_progress',
        metadata: {
          cost: totalCost,
          duration: (Date.now() - startTime) / 1000,
          model: 'sonar',
          questionsCount: questions.length,
          sourcesCount: extractedSources.length,
          stepDetails,
        } as any,
      });

      // STEP 6: Refining - Rewrite based on critique
      yield this.createEvent('refining', 'in_progress');

      const refineStart = Date.now();
      const finalResult = await this.refineDraft(draft, critique, topic);
      if (finalResult.error || !finalResult.data) {
        yield this.createEvent('refining', 'error', null, finalResult.error?.message);
        return;
      }

      const finalReport = finalResult.data.content;
      const finalCitations = finalResult.data.citations || [];
      const refiningCost = 0.01;
      totalCost += refiningCost;

      stepDetails.push({
        type: 'refining',
        status: 'complete',
        startTime: refineStart,
        endTime: Date.now(),
        cost: refiningCost,
        data: {
          outputPreview: finalReport.slice(0, 300),
          appliedSuggestions: critique.suggestions,
          missingTopicsAddressed: critique.missingTopics,
          citations: finalCitations,
          appliedValueAdded: critique.valueAdded,
        },
      });

      yield this.createEvent('refining', 'complete', {
        finalReport,
        appliedSuggestions: critique.suggestions,
        critique,
        citations: finalCitations,
      });

      // Save final results to database
      const metadata: ResearchMetadata = {
        cost: totalCost,
        duration: (Date.now() - startTime) / 1000,
        model: 'sonar',
        questionsCount: questions.length,
        sourcesCount: sources.length,
        stepDetails,
      };
      // Preserve full critique snapshot for later viewing
      (metadata as any).critique = critique;

      await researchStorageService.updateSession(sessionId, {
        status: 'complete',
        questions,
        sources: extractedSources,
        finalReport,
        metadata,
      });
    } catch (error) {
      console.error('[ResearchAgent] Error:', error);
      await researchStorageService.updateSession(sessionId, {
        status: 'failed',
      });
      throw error;
    }
  }

  /**
   * STEP 1: Generate research questions
   */
  private async generateQuestions(
    topic: string
  ): Promise<ResearchServiceResponse<ResearchQuestion[]>> {
    try {
      const prompt = `You are a research planner. Given a topic, generate 3-5 focused research questions that together provide comprehensive coverage of the topic.

Topic: "${topic}"

For each question, provide:
1. The question itself (clear and specific)
2. A brief rationale (why this question matters)

Return a JSON array of objects with "question" and "rationale" fields.

Example format:
[
  {"question": "What is X?", "rationale": "Establishes foundational understanding"},
  {"question": "How does X work?", "rationale": "Explains mechanics and process"}
]`;

      const response = await perplexityService.askQuestion(prompt, 'day');

      if (response.error || !response.data) {
        return {
          data: null,
          error: response.error || new Error('Failed to generate questions'),
        };
      }

      // Parse JSON from response
      try {
        const questions = JSON.parse(response.data.content) as ResearchQuestion[];
        return { data: questions, error: null };
      } catch {
        // Fallback: Create basic questions if JSON parsing fails
        const fallbackQuestions: ResearchQuestion[] = [
          { question: `What is ${topic}?`, rationale: 'Foundational understanding' },
          { question: `What are the latest developments in ${topic}?`, rationale: 'Recent news' },
          {
            question: `What are the key benefits and risks of ${topic}?`,
            rationale: 'Risk/reward analysis',
          },
        ];
        return { data: fallbackQuestions, error: null };
      }
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * STEP 2: Search web for sources
   */
  private async searchSources(
    questions: ResearchQuestion[]
  ): Promise<ResearchServiceResponse<ResearchSource[]>> {
    try {
      const queries = questions.map(q => q.question);

      const searchResult = await tavilyService.searchMultiple(queries, {
        maxResultsPerQuery: 3,
        searchDepth: 'basic',
      });

      if (searchResult.error || !searchResult.data) {
        return {
          data: null,
          error: searchResult.error || new Error('Search failed'),
        };
      }

      // Flatten and deduplicate sources
      const allResults = searchResult.data.flat();
      const uniqueSources = tavilyService.convertToResearchSources(allResults, 0.5);

      // Limit to top 10 sources
      const topSources = uniqueSources.slice(0, 10);

      return { data: topSources, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * STEP 3: Extract insights from each source
   */
  private async extractInsights(
    sources: ResearchSource[],
    topic: string
  ): Promise<ResearchServiceResponse<ResearchSource[]>> {
    try {
      // Process sources in parallel
      const extractionPromises = sources.map(async source => {
        const prompt = `Extract key insights from this source relevant to "${topic}":

Source: ${source.title}
Content: ${source.summary}

Provide a concise summary (2-3 sentences) of the most important information.`;

        const response = await perplexityService.askQuestion(prompt);

        if (response.data) {
          return {
            ...source,
            summary: response.data.content,
          };
        }
        return source; // Return original if extraction fails
      });

      const extractedSources = await Promise.all(extractionPromises);
      return { data: extractedSources, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * STEP 4: Draft initial report
   */
  private async draftReport(
    topic: string,
    questions: ResearchQuestion[],
    sources: ResearchSource[]
  ): Promise<ResearchServiceResponse<{ content: string; citations?: string[] }>> {
    try {
      const sourcesText = sources
        .map((s, i) => `[${i + 1}] ${s.title} (${s.sourceName})\n${s.summary}`)
        .join('\n\n');

      const prompt = `You are a research analyst. Write a comprehensive research brief on "${topic}".

Research Questions:
${questions.map(q => `- ${q.question}`).join('\n')}

Sources:
${sourcesText}

Write a well-structured report (500-800 words) with:
1. Executive Summary (2-3 sentences)
2. Key Findings (organized by research questions)
3. Sources (numbered citations)

Use markdown formatting. Be concise and factual.`;

      const response = await perplexityService.deepResearch(prompt);

      if (response.error || !response.data) {
        return {
          data: null,
          error: response.error || new Error('Failed to draft report'),
        };
      }

      return {
        data: { content: response.data.content, citations: response.data.citations as any },
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * STEP 5: Critique draft
   */
  private async critiqueDraft(
    draft: string,
    topic: string,
    questions: ResearchQuestion[]
  ): Promise<ResearchServiceResponse<DraftCritique>> {
    try {
      const prompt = `You are a senior editor reviewing a research report. Critique this draft and provide a comprehensive feedback for the follwoing:

Topic: ${topic}
Draft:
${draft}

Original Questions:
${questions.map(q => q.question).join('\n')}

Provide:
1. Overall quality: "excellent", "good", or "needs_improvement", and check for data correctness
2. Strengths (array of strengths)
3. Weaknesses (array of weaknesses
4. Suggestions for improvement (array of  specific edits)
5. Missing topics (array of topics not covered)
6. Critically think about what else could be added to the report to make it more comprehensive and valuable.

Return as JSON with fields: overallQuality, strengths, weaknesses, suggestions, missingTopics, valueAdded`;

      const response = await perplexityService.askQuestion(prompt);

      if (response.error || !response.data) {
        return {
          data: null,
          error: response.error || new Error('Failed to critique draft'),
        };
      }

      try {
        const critique = JSON.parse(response.data.content) as DraftCritique;
        return { data: critique, error: null };
      } catch {
        // Fallback critique
        const fallback: DraftCritique = {
          overallQuality: 'good',
          strengths: ['Clear structure', 'Good use of sources'],
          weaknesses: ['Could be more concise'],
          suggestions: ['Add more specific data points'],
          valueAdded: ['Add visual summaries for key findings'],
        };
        return { data: fallback, error: null };
      }
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * STEP 6: Refine draft based on critique
   */
  private async refineDraft(
    draft: string,
    critique: DraftCritique,
    topic: string
  ): Promise<ResearchServiceResponse<{ content: string; citations?: string[] }>> {
    try {
      const prompt = `You are a research analyst refining your report based on editorial feedback.

Original Draft:
${draft}

Editor Feedback:
- Overall Quality: ${critique.overallQuality}
- Weaknesses: ${critique.weaknesses.join(', ')}
- Suggestions: ${critique.suggestions.join(', ')}
${critique.missingTopics ? `- Missing Topics: ${critique.missingTopics.join(', ')}` : ''}
${critique.valueAdded && critique.valueAdded.length ? `- Value Added: ${critique.valueAdded.join(', ')}` : ''}

Rewrite the report addressing all feedback. Incorporate the "value added" enhancements to improve usefulness. Maintain markdown formatting and citations. Keep it concise (500-800 words).`;

      const response = await perplexityService.deepResearch(prompt);

      if (response.error || !response.data) {
        // If refinement fails, return original draft
        return { data: { content: draft }, error: null };
      }

      return {
        data: { content: response.data.content, citations: response.data.citations as any },
        error: null,
      };
    } catch (error) {
      // Return original draft on error
      return { data: { content: draft }, error: null };
    }
  }

  /**
   * Helper: Create research event
   */
  private createEvent(
    type: AgentStepType,
    status: 'pending' | 'in_progress' | 'complete' | 'error',
    data?: any,
    error?: string
  ): ResearchEvent {
    return {
      type,
      status,
      data,
      error,
      timestamp: Date.now(),
    };
  }
}

// Export singleton instance
export const researchAgent = new ResearchAgent();
